import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { MapProvider, useMap } from "react-map-gl/mapbox";
import { Button, Checkbox, Divider, Icon } from "semantic-ui-react";
import {
	ControlGroup,
	DataLayerDropdown,
	FitToHeight,
	FitToWidth,
	HeatmapDropdown,
	MeasureTool,
	OrthoDropdown,
	ReCenter,
	Rotate,
} from "./MapControls";
import MFAMapViewer from "./MFAMapViewer";
import {
	DataProvider,
	DrawProvider,
	GraphProvider,
	useDataDispatch,
	useGraph,
	useViewerDispatch,
	ViewerProvider,
	ZOrderProvider,
} from "./StateManagement";
import "./styles.css";

import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
	arrayMove,
	horizontalListSortingStrategy,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import Graph from "./Graph";
import { DataPresentationDropdown, ExperimentalDesignDropdown } from "./GraphControls";

const HeatmapDropdownMemo = memo(HeatmapDropdown);
const DataLayerDropdownMemo = memo(DataLayerDropdown);
const OrthoDropdownMemo = memo(OrthoDropdown);

const MFASummaryContent = () => {
	const { MFAViewer } = useMap();
	const sidePanelRef = useRef<HTMLDivElement>(null);
	const layersRef = useRef<HTMLDivElement>(null);

	const {
		updatePlotLabelsVisible,
		updatePlotsVisible,
		updateTreatmentsVisible,
		updateLayerOrder,
		updateStatusMarkersVisible,
	} = useViewerDispatch();
	const { updateHideExclusions } = useDataDispatch();
	const { chartType } = useGraph();

	const [sidePanelState, setSidePanelState] = useState<"half" | "closed" | "fullscreen">("half");
	const [plotsVisible, setPlotsVisible] = useState(true);
	const [plotLabelsVisible, setPlotLabelsVisible] = useState(false);
	const [heatmapValueVisible, setHeatmapValueVisible] = useState(false);
	const [treatmentsVisible, setTreatmentsVisible] = useState(true);
	const [statusMarkersVisible, setStatusMarkersVisible] = useState(true);
	const [resizing, setResizing] = useState(false);
	const [layersOpen, setLayersOpen] = useState(false);
	const [graphMargin, setGraphMargin] = useState(0);

	const [layers, setLayers] = useState(["heatmap", "data", "ortho"]);
	useEffect(() => updateLayerOrder(["heatmap", "data", "ortho"]), [updateLayerOrder]);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	);

	useEffect(() => {
		const handler = setTimeout(() => {
			if (sidePanelState === "half") {
				MFAViewer?.setPadding({ top: 75, left: sidePanelRef.current?.offsetWidth ?? 0 + 30, right: 10, bottom: 40 });
			} else {
				MFAViewer?.setPadding({ top: 75, left: 30, right: 10, bottom: 100 });
			}
			setResizing(false);
		}, 50);

		return () => {
			clearTimeout(handler);
		};
	}, [sidePanelState, MFAViewer, sidePanelRef, layersOpen]);

	useEffect(() => {
		if (!layersRef.current) return;

		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				setGraphMargin(entry.contentRect.width / 2);
			}
		});

		observer.observe(layersRef.current);

		return () => observer.disconnect();
	}, []);

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;

			if (over && active.id !== over.id) {
				const oldIndex = layers.indexOf(active.id as string);
				const newIndex = layers.indexOf(over.id as string);
				const newLayers = arrayMove(layers, oldIndex, newIndex);
				updateLayerOrder(newLayers);
				setLayers(newLayers);
			}
		},
		[layers, updateLayerOrder],
	);

	const LayerBlock = useCallback(({ id, layersOpen }: { id: string; layersOpen: boolean }) => {
		switch (id) {
			case "heatmap":
				return <HeatmapDropdownMemo minimized={!layersOpen} />;
			case "data":
				return <DataLayerDropdownMemo minimized={!layersOpen} />;
			case "ortho":
				return <OrthoDropdownMemo minimized={!layersOpen} />;
			default:
				return <></>;
		}
	}, []);

	return (
		<div style={{ height: "100%", width: "100%" }}>
			<MFAMapViewer />
			<ZOrderProvider>
				<div
					ref={sidePanelRef}
					className={`sidePanel ${sidePanelState}`}
					onClick={() => {
						setLayersOpen(false);
					}}
					// style={{
					// 	marginLeft: sidePanelState === "closed" ? "-49.7%" : "0",
					// 	width: sidePanelState === "fullscreen" ? "100%" : "50%"
					// }}
				>
					<Button
						className="sidePanelToggleButton right"
						icon
						onClick={() => {
							setResizing(true);
							setSidePanelState((prev) => (prev === "closed" ? "half" : "fullscreen"));
						}}
					>
						<Icon className="dropdown sidePanelToggleIcon" style={{ rotate: "-90deg" }} size="small" />
					</Button>
					<Button
						className="sidePanelToggleButton left"
						icon
						onClick={() => {
							setResizing(true);
							setSidePanelState((prev) => (prev === "fullscreen" ? "half" : "closed"));
						}}
					>
						<Icon className="dropdown sidePanelToggleIcon" style={{ rotate: "90deg" }} size="small" />
					</Button>

					<div
						className="leftMapControls"
						onClick={(e) => {
							e.stopPropagation();
						}}
					>
						<div
							ref={layersRef}
							className={
								sidePanelState === "fullscreen" ? "topBar" : `mapLayerControls controlPanel${layersOpen ? " open" : ""}`
							}
						>
							{sidePanelState !== "fullscreen" && (
								<Button
									icon
									compact
									onClick={() => {
										setResizing(true);
										setLayersOpen((prev) => !prev);
									}}
									toggle
									active={layersOpen}
								>
									<Icon className="layers" size="large" />
									<span>Layers</span>
								</Button>
							)}
							<div className="mapLayerContent">
								<DndContext
									sensors={sensors}
									collisionDetection={closestCenter}
									onDragEnd={handleDragEnd}
									// modifiers={[restrictToParentElement]}
								>
									<SortableContext
										items={layers}
										strategy={layersOpen ? verticalListSortingStrategy : horizontalListSortingStrategy}
									>
										{(sidePanelState === "fullscreen"
											? chartType === "line"
												? ["heatmap"]
												: ["heatmap", "data"]
											: layers
										).map((layer) => (
											<LayerBlock key={layer} id={layer} layersOpen={sidePanelState === "fullscreen" || layersOpen} />
										))}
									</SortableContext>
								</DndContext>
							</div>
						</div>
					</div>

					<div style={{ display: "flex", gap: 10, alignItems: "center", padding: "3px 0" }}>
						<ExperimentalDesignDropdown />
						<div style={{ flexGrow: 2 }} />
						<span style={{ width: 100, fontSize: "1em", lineHeight: 1.2, textAlign: "right" }}>
							Hide
							<br />
							Exclusions
						</span>
						<Checkbox
							toggle
							defaultChecked
							onChange={(_, { checked }) => {
								updateHideExclusions(checked ?? true);
							}}
						/>
						<DataPresentationDropdown />
					</div>
					<Divider />
					<Graph resizing={resizing} graphStyle={{ width: layersOpen ? `calc(100% - ${graphMargin}px)` : "100%" }} />
				</div>
				<div className="rightMapControls">
					<ControlGroup>
						<MeasureTool />
						<ControlGroup.Toggle
							label="Plot Labels"
							icon="table"
							active={plotLabelsVisible}
							onClick={() => {
								updatePlotLabelsVisible(!plotLabelsVisible ? "name" : "none");
								setHeatmapValueVisible(false);
								setPlotLabelsVisible((prev) => !prev);
							}}
						/>
						<ControlGroup.Toggle
							label="Heatmap Values"
							icon="map"
							active={heatmapValueVisible}
							onClick={() => {
								updatePlotLabelsVisible(!heatmapValueVisible ? "heatmap" : "none");
								setPlotLabelsVisible(false);
								setHeatmapValueVisible((prev) => !prev);
							}}
						/>
						<ControlGroup.Toggle
							label="Plots"
							icon="table"
							active={plotsVisible}
							onClick={() => {
								updatePlotsVisible(!plotsVisible);
								setPlotsVisible((prev) => !prev);
							}}
						/>
						<ControlGroup.Toggle
							label="Treatments"
							icon="expand"
							active={treatmentsVisible}
							onClick={() => {
								updateTreatmentsVisible(!treatmentsVisible);
								setTreatmentsVisible((prev) => !prev);
							}}
						/>
						<ControlGroup.Toggle
							label="Status Markers"
							icon="flag"
							active={statusMarkersVisible}
							onClick={() => {
								updateStatusMarkersVisible(!statusMarkersVisible);
								setStatusMarkersVisible((prev) => !prev);
							}}
						/>
						<FitToHeight />
						<FitToWidth />
						<Rotate direction="right" />
						<Rotate direction="left" />
						<ReCenter />
					</ControlGroup>
					{/* <div className="cameraControls"></div> */}
				</div>
			</ZOrderProvider>
		</div>
	);
};

const MemoizedContent = memo(MFASummaryContent);

const MFASummary = () => {
	return (
		<DataProvider>
			<DrawProvider>
				<GraphProvider>
					<MapProvider>
						<ViewerProvider>
							<MemoizedContent />
						</ViewerProvider>
					</MapProvider>
				</GraphProvider>
			</DrawProvider>
		</DataProvider>
	);
};

export default MFASummary;
